// Canonical-first location presentation helpers.
// Pure functions for create/edit summary, PropertyCard, DetailHero, DetailLocation.
// Matches backend GEO_CATEGORY_LABELS_UK exactly; does not mutate inputs.

export const GEO_CATEGORY_LABELS_UK = {
  O: "Область / Автономна Республіка Крим",
  K: "Місто зі спеціальним статусом",
  P: "Район",
  H: "Територіальна громада",
  M: "Місто",
  C: "Село",
  X: "Селище",
  B: "Район у місті",
  T: "Селище міського типу",
};

const SETTLEMENT_CATEGORIES = new Set(["M", "C", "X", "T", "K"]);
const ADMIN_CONTEXT_CATEGORIES = new Set(["P", "H", "B"]);

const EMPTY_PRESENTATION = Object.freeze({
  isCanonical: false,
  country: "",
  region: "",
  settlement: "",
  settlementCategory: null,
  settlementCategoryLabel: "",
  administrativeContext: Object.freeze([]),
  administrativeContextLine: "",
  localArea: "",
  address: "",
  hierarchyRows: Object.freeze([]),
  compactLine: "",
  fullLine: "",
});

function trimText(value) {
  return String(value ?? "").trim();
}

export function resolveCategoryLabel(item) {
  if (!item || typeof item !== "object") return "";
  const provided = trimText(item.category_label);
  if (provided) return provided;
  const category = item.category;
  if (category && GEO_CATEGORY_LABELS_UK[category]) {
    return GEO_CATEGORY_LABELS_UK[category];
  }
  return "";
}

export function dedupePathByCode(path) {
  if (!Array.isArray(path)) return [];
  const seen = new Set();
  const result = [];
  for (const item of path) {
    if (!item || typeof item !== "object") continue;
    const code = item.code;
    if (code) {
      if (seen.has(code)) continue;
      seen.add(code);
    }
    result.push(item);
  }
  return result;
}

/**
 * Format one hierarchy row for create/edit summary.
 * Uses factual API names + category-aware wording for canonical units only.
 * Never appends "обл." to arbitrary legacy text.
 */
export function formatHierarchyDisplayLine(item) {
  if (!item || typeof item !== "object") return "";
  const name = trimText(item.name);
  if (!name) return "";
  const category = item.category;
  const label = resolveCategoryLabel(item);

  if (category === "K") {
    return name;
  }

  if (category === "M" || category === "C" || category === "X" || category === "T") {
    return label ? `${label} ${name}` : name;
  }

  if (category === "O") {
    if (/область|крим|республіка/i.test(name)) return name;
    return `${name} область`;
  }
  if (category === "P") {
    if (/район/i.test(name)) return name;
    return `${name} район`;
  }
  if (category === "H") {
    if (/громад/i.test(name)) return name;
    return `${name} територіальна громада`;
  }
  if (category === "B") {
    if (/район/i.test(name)) return name;
    return `${name} район у місті`;
  }

  return name;
}

export function buildHierarchyRows(unit) {
  if (!unit || typeof unit !== "object") return [];
  const path = dedupePathByCode(unit.path);
  let items = path.length > 0 ? path : [unit];

  // Ensure the selected unit appears exactly once (by code).
  if (unit.code) {
    const hasUnit = items.some((item) => item.code === unit.code);
    if (!hasUnit) {
      items = [...items, unit];
    }
  }

  // Special-status city: render once even if duplicated in path.
  if (unit.category === "K") {
    items = dedupePathByCode(
      items.filter((item) => item.category === "K" || item.code === unit.code),
    );
    if (items.length === 0) items = [unit];
  }

  return items
    .map((item) => {
      const name = trimText(item.name);
      const displayLine = formatHierarchyDisplayLine(item);
      if (!displayLine) return null;
      return {
        code: item.code || null,
        category: item.category || null,
        categoryLabel: resolveCategoryLabel(item),
        name,
        displayLine,
      };
    })
    .filter(Boolean);
}

function resolveCanonicalUnit(location) {
  if (!location || typeof location !== "object") return null;
  if (location.settlement_unit && typeof location.settlement_unit === "object") {
    return location.settlement_unit;
  }
  if (
    location.selectedSettlement
    && typeof location.selectedSettlement === "object"
    && location.selectedSettlement.code
  ) {
    return location.selectedSettlement;
  }
  return null;
}

function namedObjectFromLocation(location) {
  const obj = location.address_object;
  if (obj && typeof obj === "object") {
    const name = trimText(obj.name);
    if (name) return name;
  }
  return trimText(location.manual_address_object);
}

function streetAddressFromLocation(location) {
  const named = namedObjectFromLocation(location);
  const building = trimText(location.building_number);
  if (named && building) return `${named}, ${building}`;
  if (named) return named;
  if (building) return building;
  return "";
}

function localAreaFromLocation(location) {
  return trimText(location.local_area);
}

function buildStructuredWithoutUnit(location) {
  const region = trimText(location.region_name);
  const settlement = trimText(location.settlement_name);
  const localArea = localAreaFromLocation(location);
  const address = streetAddressFromLocation(location);
  const formatted = trimText(location.formatted_address);

  const compactParts = [];
  if (settlement) compactParts.push(settlement);
  else if (region) compactParts.push(region);
  if (localArea) compactParts.push(localArea);

  const fullParts = [];
  if (region && region !== settlement) fullParts.push(region);
  if (settlement) fullParts.push(settlement);
  if (localArea) fullParts.push(localArea);
  if (address) fullParts.push(address);

  return {
    ...EMPTY_PRESENTATION,
    isCanonical: false,
    country: settlement || region ? "Україна" : "",
    region,
    settlement,
    localArea,
    address,
    compactLine: compactParts.join(" • "),
    fullLine: formatted || fullParts.join(", "),
  };
}

function buildCanonicalPresentation(location, unit) {
  const localArea = localAreaFromLocation(location);
  const address = streetAddressFromLocation(location);
  const formatted = trimText(location.formatted_address);
  const path = dedupePathByCode(unit.path);
  const effectivePath = path.length > 0 ? path : [unit];

  const root =
    effectivePath.find((item) => item.category === "O" || item.category === "K")
    || (unit.category === "O" || unit.category === "K" ? unit : null);

  const settlementUnit =
    SETTLEMENT_CATEGORIES.has(unit.category) ? unit : (
      [...effectivePath].reverse().find((item) => SETTLEMENT_CATEGORIES.has(item.category))
      || unit
    );

  const rootCode = root?.code || null;
  const settlementCode = settlementUnit?.code || null;

  const regionName = trimText(root?.name);
  const settlementName = trimText(settlementUnit?.name);
  const settlementCategory = settlementUnit?.category || null;
  const settlementCategoryLabel = resolveCategoryLabel(settlementUnit);

  const administrativeContext = effectivePath
    .filter((item) => ADMIN_CONTEXT_CATEGORIES.has(item.category))
    .filter((item) => {
      if (!item.code) return true;
      if (rootCode && item.code === rootCode) return false;
      if (settlementCode && item.code === settlementCode) return false;
      return true;
    })
    .map((item) => ({
      code: item.code || null,
      name: trimText(item.name),
      category: item.category,
      categoryLabel: resolveCategoryLabel(item),
    }))
    .filter((item) => item.name);

  const administrativeContextLine = administrativeContext
    .map((item) => item.name)
    .filter(Boolean)
    .join(", ");

  // Canonical country is always Ukraine for KATOTTG-backed rows.
  const country = "Україна";

  const compactParts = [];
  if (settlementName) compactParts.push(settlementName);
  if (localArea) compactParts.push(localArea);

  const fullParts = [];
  const regionDistinct =
    regionName
    && settlementName
    && !(rootCode && settlementCode && rootCode === settlementCode)
    && regionName !== settlementName;
  if (regionDistinct) fullParts.push(regionName);
  else if (regionName && !settlementName) fullParts.push(regionName);
  if (settlementName) fullParts.push(settlementName);
  if (localArea) fullParts.push(localArea);
  if (address) fullParts.push(address);

  return {
    isCanonical: true,
    country,
    region: regionName,
    settlement: settlementName,
    settlementCategory,
    settlementCategoryLabel,
    administrativeContext,
    administrativeContextLine,
    localArea,
    address,
    hierarchyRows: buildHierarchyRows(unit),
    compactLine: compactParts.join(" • "),
    fullLine: formatted || fullParts.join(", "),
  };
}

/**
 * Structured canonical-first presentation contract for location surfaces.
 * Does not mutate the input location object.
 */
export function buildLocationPresentation(location) {
  if (!location || typeof location !== "object") {
    return { ...EMPTY_PRESENTATION, administrativeContext: [], hierarchyRows: [] };
  }

  const unit = resolveCanonicalUnit(location);
  if (!unit) {
    return buildStructuredWithoutUnit(location);
  }

  return buildCanonicalPresentation(location, unit);
}

/** Compact card line — canonical settlement first, then optional local area. */
export function buildCompactLocationLine(location) {
  return buildLocationPresentation(location).compactLine;
}

/** Full hero/detail line — no invented "обл." suffixes. */
export function buildFullLocationLine(location) {
  return buildLocationPresentation(location).fullLine;
}
